import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TagIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { depositsAPI } from '../services/api';
import {
  LoadingSpinner,
  EmptyState,
  StatCard,
  Badge,
  SearchInput,
  FilterButton,
  DataTable,
  Pagination,
} from '../components/common';
import { formatCurrency, formatDateTime, formatPhone } from '../utils/formatters';

export default function DepositsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: depositsData, isLoading } = useQuery({
    queryKey: ['deposits', searchTerm, statusFilter, currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const response = await depositsAPI.getHistory(params);
      return response.data;
    },
  });

  const deposits = depositsData?.data || [];
  const pagination = depositsData?.pagination || { total: 0, pages: 1 };

  const columns = [
    {
      key: 'id',
      label: 'Transaction ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'user_phone',
      label: 'User',
      render: (value, row) => formatPhone(value || row.user_id),
    },
    {
      key: 'amount_cents',
      label: 'Amount',
      render: (value) => formatCurrency(value || 0),
    },
    {
      key: 'payment_method',
      label: 'Method',
      render: (value) => value || 'Manual',
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant={
          value === 'completed' ? 'success' :
          value === 'pending' ? 'warning' :
          'danger'
        }>
          {value}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (value) => formatDateTime(value),
    },
    {
      key: 'transaction_reference',
      label: 'Reference',
      render: (value) => value || '-',
    },
  ];

  const stats = {
    total: pagination.total || 0,
    completed: deposits?.filter(d => d.status === 'success').length || 0,
    pending: deposits?.filter(d => d.status === 'pending').length || 0,
    failed: deposits?.filter(d => d.status === 'failed').length || 0,
    refunded: deposits?.filter(d => d.status === 'refunded').length || 0,
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading deposits..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Deposits</h1>
          <p className="mt-2 text-sm text-gray-600">
            Track and manage member deposit transactions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Deposits"
          value={stats.total}
          icon={CurrencyDollarIcon}
          color="primary"
        />
        <StatCard
          title="Completed"
          value={stats.completed}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          title="Pending"
          value={stats.pending}
          icon={ClockIcon}
          color="yellow"
        />
        <StatCard
          title="Failed"
          value={stats.failed}
          icon={XCircleIcon}
          color="red"
        />
        <StatCard
          title="Refunded"
          value={stats.refunded}
          icon={CheckIcon}
          color="black"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
          placeholder="Search by transaction ID or user..."
          className="flex-1"
        />
        <div className="flex gap-2 flex-wrap">
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === 'completed'}
            onClick={() => setStatusFilter('completed')}
          >
            Completed
          </FilterButton>
          <FilterButton
            active={statusFilter === 'pending'}
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </FilterButton>
          <FilterButton
            active={statusFilter === 'failed'}
            onClick={() => setStatusFilter('failed')}
          >
            Failed
          </FilterButton>
        </div>
      </div>

      <div className="card">
        {deposits && deposits.length > 0 ? (
          <>
            <DataTable columns={columns} data={deposits} />
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
            />
          </>
        ) : (
          <EmptyState
            icon={CurrencyDollarIcon}
            title="No deposits found"
            description={searchTerm || statusFilter !== 'all' 
              ? "Try adjusting your filters" 
              : "Deposit transactions will appear here"
            }
          />
        )}
      </div>
    </div>
  );
}
